import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { Team } from '../team.type';
import { TeamMember } from './team-member.type';

@Injectable({
    providedIn: 'root'
})
export class TeamMemberService {
    constructor(private http: HttpClient) {
    }

    async query(teamId: string) {
        return await firstValueFrom(this.http.get<TeamMember[]>('/api/team/' + teamId + '/teammembers'));
    }

    create(teamId: string, user: TeamMember) {
        return this.http.post<TeamMember>('/api/team/' + teamId + '/teammembers', user);
    }

    async update(teamId: string, userId: string, teamMember: TeamMember) {
        return await firstValueFrom(
            this.http.put<{message: string, error: boolean}>('/api/team/' + teamId + '/teammembers/' + userId, teamMember).pipe(
                map(_ => {
                    return {
                        message: 'changeSaved',
                        error: false
                    };
                }),
                catchError(error => {
                    return of({
                        message: error.error.error,
                        error: true
                    });
                })
            )
        );
    }

    delete(teamId: string, userId: string) {
        return this.http.delete('/api/team/' + teamId + '/teammembers/' + userId);
    }

    async move(teamId: string, userId: string, moveToId: string) {
        return await firstValueFrom(this.http.put<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/move/' + moveToId, {}));
    }

    async getAvailableTeamsForMove(userId: string, teamId: string) {
        return await firstValueFrom(this.http.get<Team[]>('/api/team/' + teamId + '/teammembers/' + userId + '/availableTeamsForMove'));
    }

    async toggle(teamId: string, userId: string) {
        return await firstValueFrom(this.http.put<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/toggle', {}));
    }

    async update_roles(teamId: string, userId: string, roles: string[]) {
        return await firstValueFrom(this.http.put<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/roles/', {roles: roles}));
    }

    async getTags(teamId: string) {
        return await firstValueFrom(this.http.get<string[]>('/api/team/' + teamId + '/tags'));
    }

    async addTag(teamId: string, userId: string, tag: string) {
        return await firstValueFrom(this.http.put<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/tag/' + tag, {}));
    }

    async removeTag(teamId: string, userId: string, tag: string) {
        return await firstValueFrom(this.http.delete<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/tag/' + tag));
    }

    async toggleChannel(teamId: string, userId: string, channel: string) {
        return await firstValueFrom(this.http.put<TeamMember>('/api/team/' + teamId + '/teammembers/' + userId + '/toggle_channel/' + channel, {}));
    }

    async batchUpdate(team_members: TeamMember[], teamId: string, field: string, value: any) {

        if (field === 'enable' || field === 'disable') {
            value = 'nothing';
        }
        
        if (field === 'working_days') {
            if(!value) {
                value = '0000000';
            }else{
                const convertBoolToInt = (bool: boolean): string => {
                    return bool ? "1" : "0";
                };
                value = convertBoolToInt(value.monday) +
                    convertBoolToInt(value.tuesday) +
                    convertBoolToInt(value.wednesday) +
                    convertBoolToInt(value.thursday) +
                    convertBoolToInt(value.friday) +
                    convertBoolToInt(value.saturday) +
                    convertBoolToInt(value.sunday);
            }
        }

        const team_members_id = team_members.map(function (member) {
            return member.id;
        });

        return await firstValueFrom(this.http.post('/api/team/' + teamId + '/teammembers/batch', {
            team_members_id: team_members_id,
            property: field,
            value: value
        }));
    }
}

